// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import Alert from "src/components/shared/alerts/Alert/Alert";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import CustomAutocomplete from "src/components/shared/inputs/CustomAutocomplete/CustomAutocomplete";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

// Types
import { PatientType } from "src/services/patients/types";

// Utils
import { getTruncatedText } from "src/utils/text/textHelper";

// Hooks
import { usePatientsData } from "src/services/patients/hooks/usePatientsData";
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useExamTransferContext } from "src/contexts/exams/ExamTransferContext";

// Services
import { getProgressionAnalysisForExams } from "src/services/exams/api";

type ExamTransferModalType = {
  open: boolean;
  currentPatient: PatientType;
  onClose: () => void;
  onConfirm: (props: {
    patientId: string;
    examId: string | undefined;
    newPatientId: string;
  }) => void;
};

const ExamTransferModal = ({
  open,
  currentPatient,
  onClose,
  onConfirm,
}: ExamTransferModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { getToken } = useTokenAccess();
  const { clinicInfo } = useClinicContext();

  const { transferPatient, transferExamId, actions } = useExamTransferContext();
  const { setTransferPatient } = actions;

  const [progressionAnalysisCount, setProgressionAnalysisCount] = useState(0);

  const formattedPatientName = useMemo(
    () =>
      getTruncatedText(
        `${currentPatient.firstName} ${currentPatient.lastName}`
      ),
    [currentPatient.firstName, currentPatient.lastName]
  );

  const {
    data: patientsOptions,
    isLoading: isLoadingPatients,
    isFetching: isFetchingPatients,
    isRefetching: isRefetchingPatients,
  } = usePatientsData(clinicInfo?.id);

  const isLoading = useMemo(
    () => isLoadingPatients || isFetchingPatients || isRefetchingPatients,
    [isLoadingPatients, isFetchingPatients, isRefetchingPatients]
  );

  // show all patients in the dropdown except for the current patient
  const formattedPatientsOptions = useMemo(
    () =>
      (patientsOptions ?? [])
        .filter((option) => option.id !== currentPatient.id)
        .map((option) => {
          const patientName = getTruncatedText(
            `${option?.firstName} ${option?.lastName}`
          );
          const birthdate = option?.dateOfBirth?.slice(0, 10);
          return {
            displayName: `${patientName} / ${birthdate}`,
            value: option.id,
          };
        })
        .sort((a, b) => a?.displayName?.localeCompare(b?.displayName)),
    [patientsOptions, currentPatient.id]
  );

  // disable the button if the selected patient isn't a valid dropdown option
  const shouldDisable = useMemo(() => {
    return (
      isLoading ||
      !patientsOptions?.some((patient) => patient?.id === transferPatient.value)
    );
  }, [isLoading, patientsOptions, transferPatient.value]);

  const handleSetPatient = useCallback(
    (item: { displayName: string; value: string }) => {
      const value = formattedPatientsOptions?.find(
        (option) => option.value === item.value
      );

      if (value) {
        setTransferPatient({ ...value });
      } else {
        setTransferPatient({ displayName: "", value: "" });
      }
    },
    [formattedPatientsOptions, setTransferPatient]
  );

  // refetch the related progression analyses every time the patient, clinic, or selected exams change
  const fetchAnalyses = useCallback(async () => {
    const token = await getToken();
    getProgressionAnalysisForExams(token, clinicInfo?.id, currentPatient.id, [
      transferExamId,
    ]).then((res) => {
      setProgressionAnalysisCount(res.length);
    });
  }, [clinicInfo?.id, currentPatient.id, transferExamId, getToken]);

  useEffect(() => {
    if (open) {
      fetchAnalyses();
    }
  }, [fetchAnalyses, open]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("Reassign Exam to Another Patient")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            disabled={shouldDisable}
            sx={ModalCustomConfirm}
            onClick={() => {
              onConfirm({
                patientId: currentPatient.id,
                examId: transferExamId,
                newPatientId: transferPatient.value,
              });
            }}
          >
            {t("Confirm")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="20px">
        {progressionAnalysisCount > 0 && (
          <Alert
            variant="warning"
            alertContent={t(
              "This exam is associated with a progression analysis report. Transferring it may impact the report."
            )}
          />
        )}

        <Alert
          variant="warning"
          alertContent={t(
            "Ensure that your EMR system is updated by removing the old report from the previous patient's record."
          )}
        />

        <Box display="flex" flexDirection="column" gap="4px">
          <Typography fontSize="14px" fontWeight={600}>
            {t("Current Patient")}:
          </Typography>
          <Typography fontSize="14px">{`${formattedPatientName} / ${currentPatient?.dateOfBirth?.slice(0, 10)}`}</Typography>
        </Box>

        <Typography fontSize="14px">
          {t("Select the patient that the exam should be transferred to")}:
        </Typography>

        <Box display="flex" flexDirection="column" gap="4px">
          <Typography fontSize="14px">
            {t("Patient")}
            <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
          </Typography>
          <CustomAutocomplete
            id="exam-transfer-patient-autocomplete"
            data={formattedPatientsOptions}
            value={transferPatient}
            onChange={handleSetPatient}
          />
        </Box>
      </Grid>
    </DefaultModal>
  );
};

export default ExamTransferModal;
